// ============================================
// gsap scrollTrigger
// ============================================
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
gsap.registerPlugin(ScrollTrigger, SplitText);
import Lenis from 'lenis';

// モバイルのアドレスバー伸縮による高コストな全 ScrollTrigger 再計算を抑止
ScrollTrigger.config({ ignoreMobileResize: true });

// ============================================
// Lenis
// ============================================
export let lenis = new Lenis({
  lerp: 0.09,
  smoothWheel: true,
  syncTouch: false,
  wheelMultiplier: 0.9,
});

lenis.on('scroll', ScrollTrigger.update);

const _raf = (time) => lenis.raf(time * 1000);
gsap.ticker.add(_raf);

gsap.ticker.lagSmoothing(0);
window.addEventListener('pageshow', (e) => {
  if (e.persisted) ScrollTrigger.refresh();
});

// ============================================
// ページ内リンク
// ============================================
function lenisPageScroll() {
  const links = document.querySelectorAll('a[href^="#"]:not(.-no-smooth, .l-header__skip-content)');
  if (links.length === 0) return;

  // 「 80px」のような空白付き・単位付きの値でも数値化できるようにする
  const headerHeight = getComputedStyle(document.documentElement).getPropertyValue('--size-header-h');
  const headerHeightNum = parseFloat(headerHeight) || 0;

  links.forEach((target) => {
    target.addEventListener('click', (e) => {
      e.preventDefault();
      let targetLink = target.getAttribute('href');
      if (targetLink === '#') {
        targetLink = 'body';
      }

      // lerp を渡すと duration/easing が無視されるため duration ベースに統一する
      lenis.scrollTo(targetLink, {
        offset: headerHeightNum * -1,
        duration: 1.4,
        easing: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)), // easeOutExpo
      });
    });
  });
}

// ============================================
// スクロールアニメーション
// ============================================
function inViewAnm() {
  ScrollTrigger.batch('.js-fade', {
    start: 'top 75%',
    end: 'bottom 75%',
    once: true,
    onEnter: (batch) => {
      batch.forEach((el) => el.classList.add('is-active'));
    },
  });
}

// ============================================
// heading（文字単位）
// ============================================
function headAnim() {
  const headings = gsap.utils.toArray('.js-head');
  if (!headings.length) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  headings.forEach((heading) => {
    const split = new SplitText(heading, {
      type: 'words,chars',
      wordsClass: '-head-word',
      charsClass: '-head-char',
      mask: 'chars',
      tag: 'span',
      aria: 'auto',
    });

    if (reduceMotion) {
      gsap.set(split.chars, { autoAlpha: 1, yPercent: 0 });
      return;
    }

    gsap.fromTo(
      split.chars,
      { autoAlpha: 0, yPercent: 50 },
      {
        autoAlpha: 1,
        yPercent: 0,
        duration: 0.9,
        ease: 'power2.out',
        stagger: 0.025,
        scrollTrigger: {
          trigger: heading,
          start: 'top 75%',
          once: true,
        },
      }
    );
  });
}

// ============================================
// 背景反転
// ============================================
function bgBlack() {
  const header = document.querySelector('.js-header');
  const targets = gsap.utils.toArray('.js-bg-black');
  if (!header || !targets.length) return;

  const activeSections = new Set();

  // 実際の文字色は CSS 側で制御する(.is-bg-black / .is-mega-open の優先順)
  // メガメニュー展開中もスクロール状態はここで更新され続けるため、
  // メニューを閉じた瞬間に現在地に応じた色へ自動で戻る
  const toggleHeaderColor = () => {
    header.classList.toggle('is-bg-black', activeSections.size > 0);
  };

  targets.forEach((target) => {
    ScrollTrigger.create({
      trigger: target,
      start: 'top 5%',
      end: 'bottom 5%',
      onToggle: ({ isActive }) => {
        if (isActive) {
          activeSections.add(target);
        } else {
          activeSections.delete(target);
        }

        toggleHeaderColor();
      },
    });
  });

  toggleHeaderColor();
}

// ============================================
// number count up
// ============================================
function numberCountUp() {
  const targets = gsap.utils.toArray('.js-number');
  if (!targets.length) return;

  const formatNumber = (value, decimalPlaces, useGrouping) => {
    const fixedValue = value.toFixed(decimalPlaces);
    if (!useGrouping) return fixedValue;

    const [integerPart, decimalPart] = fixedValue.split('.');
    const formattedInteger = Number(integerPart).toLocaleString('en-US');
    return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  };

  targets.forEach((target) => {
    const originalText = target.textContent.trim();
    const normalizedValue = originalText.replace(/,/g, '');
    const endValue = Number(normalizedValue);

    if (Number.isNaN(endValue)) return;

    const decimalPart = normalizedValue.split('.')[1];
    const decimalPlaces = decimalPart ? decimalPart.length : 0;
    const useGrouping = originalText.includes(',');
    const state = { value: 0 };

    target.textContent = formatNumber(0, decimalPlaces, useGrouping);

    gsap.to(state, {
      value: endValue,
      duration: 1.2,
      ease: 'power2.out',
      snap: decimalPlaces === 0 ? { value: 1 } : undefined,
      onUpdate: () => {
        target.textContent = formatNumber(state.value, decimalPlaces, useGrouping);
      },
      scrollTrigger: {
        trigger: target,
        start: 'top 75%',
        once: true,
      },
    });
  });
}

// ============================================
// MV
// ============================================
function mvAnim() {
  const loading = document.querySelector('.p-loading');
  if (!loading) return;

  const bg = loading.querySelector('.p-loading__bg');
  const split = loading.querySelector('.p-loading__split');

  const tl = gsap.timeline({
    defaults: { ease: 'none' },
    scrollTrigger: {
      trigger: loading,
      start: 'top top',
      end: '+=400%',
      scrub: 1.0,
    },
  });

  tl.to(bg, {
    scaleY: 1,
    duration: 0.75,
  }).to(bg, {
    scaleX: 1,
    duration: 1,
  });
  gsap.utils.toArray('.p-loading__anim').forEach((anim, index) => {
    tl.to(
      anim,
      {
        scale: 1,
        duration: 1,
        force3D: true,
        ease: 'power2.out',
      },
      index === 0 ? '-=0.5' : '-=0.7'
    );
  });
  tl.to(
    split,
    {
      scale: 1,
      duration: 1.2,
      ease: 'power2.out',
    },
    '-=0.6'
  );
  gsap.utils.toArray('.p-loading__anim, .p-loading__bg, .p-loading__title, .p-loading__img').forEach((img) => {
    tl.to(img, {
      alpha: 0,
      duration: 0,
    });
  });
  tl.to(loading, {
    pointerEvents: 'none',
    duration: 0,
  });
  tl.to(loading, {
    clipPath: 'polygon(0% 0%, 0% 100%, 0% 100%, 0% 0%, 100% 0%, 100% 100%, 100% 100%, 100% 0%)',
    duration: 1,
  });
  tl.to(
    split.querySelector('picture:first-child'),
    {
      xPercent: -100,
      duration: 1,
    },
    '<'
  );
  tl.to(
    split.querySelector('picture:last-child'),
    {
      xPercent: 100,
      duration: 1,
    },
    '<'
  );
}

// ============================================
// second loading(2回目以降の訪問)
// ============================================
function secondLoadingAnim() {
  const loading = document.querySelector('.p-second-loading');
  if (!loading) return;

  const left = loading.querySelector('.p-second-loading__img.-left');
  const right = loading.querySelector('.p-second-loading__img.-right');
  if (!left || !right) return;

  const leftImg = left.querySelector('img');
  const rightImg = right.querySelector('img');

  // 調整用の設定値(ここをいじるだけで挙動を調整できる)
  const config = {
    maskSize: 50, // 初期マスク量(%)。50 で左右がちょうど半分ずつ表示される
    scrollLength: '+=150%', // 開ききるまでのスクロール量
    scrub: 1.0, // スクロール追従の滑らかさ
    ease: 'none', // clip-path のイージング
    parallax: 12, // パララックスの移動量(%)。大きいほど画像が大きく動く
    parallaxEase: 'power1.in', // clip-path より遅れて動かし、継ぎ目のズレを隠すためのイージング
  };

  // 閉じた状態: 左の画像は左半分、右の画像は右半分だけを表示する
  // inset(top right bottom left) で見せる範囲を切り抜く
  gsap.set(left, { clipPath: `inset(0% ${config.maskSize}% 0% 0%)` });
  gsap.set(right, { clipPath: `inset(0% 0% 0% ${config.maskSize}%)` });

  const tl = gsap.timeline({
    defaults: { ease: config.ease },
    scrollTrigger: {
      trigger: loading,
      start: 'top top',
      end: config.scrollLength,
      scrub: config.scrub,
    },
  });

  // スクロールに合わせて中央から外側へマスクを開き、背面を見せていく
  tl.to(left, { clipPath: 'inset(0% 100% 0% 0%)' }, 0);
  tl.to(right, { clipPath: 'inset(0% 0% 0% 100%)' }, 0);

  // 画像は拡大せず原寸のまま動かす(拡大すると左右の継ぎ目がずれて二重に見えるため)。
  // 開始位置 0 で左右がぴったり繋がり、clip-path が継ぎ目側から先に開くので、
  // 動かして生じる隙間は常にマスクの外側に隠れて見えない。
  // ※隙間を継ぎ目側へ逃がすため、左画像は左へ・右画像は右へ動かす向きで固定。
  // ※clip-path より遅い parallaxEase で動かすことで継ぎ目のズレをさらに目立たなくする。
  if (leftImg && rightImg) {
    tl.to(leftImg, { xPercent: -config.parallax, ease: config.parallaxEase }, 0);
    tl.to(rightImg, { xPercent: config.parallax, ease: config.parallaxEase }, 0);
  }
}

// ============================================
// service
// ============================================
function serviceAnim() {
  const target = document.querySelector('.p-home__service-grid');
  if (!target) return;

  gsap.to('.p-home__service-item', {
    z: 0,
    opacity: 1,
    ease: 'power3.out',
    stagger: {
      each: 0.05,
      from: 'random',
    },
    scrollTrigger: {
      trigger: target,
      start: 'top center',
      end: '+=100%',
      scrub: true,
    },
  });
}

// ============================================
// innovate
// ============================================
function innovateAnim() {
  const target = document.querySelector('.p-home__innovate');
  if (!target) return;

  const words = target.querySelectorAll('.p-home__innovate-title');
  words.forEach((word) => {
    const split = new SplitText(word, {
      type: 'lines,words',
      wordsClass: '-word',
      linesClass: '-line',
    });
    gsap.to(split.words, {
      y: 0,
      duration: 1.2,
      ease: 'power2.out',
      stagger: 0.11,
      scrollTrigger: {
        trigger: target,
        start: () => 'top center',
      },
    });
  });

  const images = target.querySelectorAll('.-img');
  images.forEach((img) => {
    gsap.to(img.querySelector('img'), {
      y: '20%',
      scrollTrigger: {
        trigger: img,
        start: () => 'top bottom',
        end: () => 'top top',
        scrub: true,
      },
    });
  });
}

// ============================================
// recruit
// ============================================
function recruitAnim() {
  const target = document.querySelector('.p-home__recruit');
  if (!target) return;

  const tl = gsap.timeline({
    defaults: {
      ease: 'none',
    },
    scrollTrigger: {
      trigger: target,
      start: () => 'top top',
      end: () => '+=100%',
      scrub: true,
      pin: true,
    },
  });

  tl.to(target.querySelector('.-left'), {
    x: '-150%',
    rotateY: '60deg',
  });
  tl.to(
    target.querySelector('.-right'),
    {
      x: '150%',
      rotateY: '-60deg',
    },
    0
  );

  // 背景テキストの広がりは width(毎フレーム レイアウト計算が発生)ではなく
  // transform で表現する(CSS 側は width: 100% 固定)
  // ※ScrollTrigger 付きツイーンはタイムラインにネストせず、トップレベルに置く
  const bg = target.querySelector('.p-home__recruit-bg');
  if (bg) {
    const bgTrigger = {
      trigger: target,
      start: 'top top',
      end: 'bottom top',
      scrub: true,
    };
    gsap.fromTo(bg.querySelector('.-start'), { x: '25vw' }, { x: 0, ease: 'none', scrollTrigger: bgTrigger });
    gsap.fromTo(bg.querySelector('.-end'), { x: '-25vw' }, { x: 0, ease: 'none', scrollTrigger: { ...bgTrigger } });
  }
}

// ============================================
// contact
// ============================================
function contactAnim() {
  const targets = gsap.utils.toArray('.js-contact-section');
  if (!targets.length) return;

  targets.forEach((target) => {
    const bg = target.querySelector('.js-contact-bg');
    const images = gsap.utils.toArray(target.querySelectorAll('.js-contact-anim')).reverse();
    if (!bg || !images.length) return;

    const tl = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: {
        trigger: target,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.8,
      },
    });

    images.forEach((anim, index) => {
      tl.to(
        anim,
        {
          scale: 0,
          duration: 1.5,
          ease: 'power2.out',
        },
        index === 0 ? 0 : '-=1.2'
      );
    });

    tl.to(
      bg,
      {
        scaleX: window.innerWidth <= 1024 ? 0.01 : 0.005,
        duration: 1.3,
      },
      '-=1.4'
    );
    tl.to(
      bg,
      {
        scaleY: 0,
        duration: 1,
      },
      '-=0.2'
    );
    tl.to({}, { duration: 0.6 });
  });
}

export { lenisPageScroll, inViewAnm, headAnim, numberCountUp, mvAnim, secondLoadingAnim, serviceAnim, innovateAnim, recruitAnim, contactAnim, bgBlack };
